import { RowSelectionState, SortingState } from "@tanstack/react-table";
import moment from "moment";
import { useCustomValuePreviewActions } from "PFApp/components/custom_value_preview/use_custom_value_preview";
import { ProfileCustomValuePill } from "PFApp/components/profile_custom_value/profile_custom_value_pill";
import { useCustomValueTags } from "PFApp/hooks/use_custom_value_tags";
import { useExperienceConfig } from "PFApp/hooks/use_experience_config";
import { useExperienceLabelMap } from "PFApp/hooks/use_experience_label_map";
import { Button } from "PFComponents/button";
import { CustomValuePill } from "PFComponents/custom_value_pill";
import Table from "PFComponents/table";
import { ColumnHelper } from "PFComponents/table/table.types";
import { Typography } from "PFComponents/typography";
import { useDateFormatter, useSkillsType } from "PFCore/hooks";
import { ActivityCustomValue, CustomValueScope, Id, ProfileCustomValue } from "PFCore/types";
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

export type SkillsTableProps = {
  skills: (ProfileCustomValue | ActivityCustomValue)[];
  profileId: Id;
  noInitialSorting?: boolean;
  onSortingChange?: (sorting: SortingState) => void;
  controlledSorting?: SortingState;
  maxNameLength?: number;
  allowSkillPreview?: boolean;
  canEditSkills?: boolean;
  rowSelectionState?: RowSelectionState;
  setRowSelectionState?: (state: RowSelectionState) => void;
  className?: string;
};

export const SkillsTable = ({
  skills,
  profileId,
  canEditSkills,
  rowSelectionState,
  setRowSelectionState,
  noInitialSorting,
  controlledSorting,
  onSortingChange,
  maxNameLength = 60,
  allowSkillPreview,
  className
}: SkillsTableProps) => {
  const { t } = useTranslation("profiles", { keyPrefix: "show.parts.skillsSection.table" });
  const tableRef = useRef<HTMLDivElement>(null);
  const { formatDate } = useDateFormatter();
  const { showPreview } = useCustomValuePreviewActions();
  const { experienceLevels } = useExperienceConfig();
  const experienceLabelMap = useExperienceLabelMap();
  const skillsType = useSkillsType();
  const { getCustomValueTags } = useCustomValueTags();

  const onMissingSkillClick = useCallback(
    (skillId: Id) => {
      showPreview({
        customTypeId: skillsType!.id,
        customValueId: skillId,
        scope: CustomValueScope.Profile,
        profileId,
        canEdit: true,
        isNew: true
      });
    },
    [showPreview, skillsType, profileId]
  );

  const scrollToTop = () => {
    setTimeout(() => {
      tableRef?.current?.scrollIntoView();
    }, 0);
  };

  const handleSortingChange = (newSortingState) => {
    onSortingChange?.(newSortingState);
    scrollToTop();
  };

  const handleSimplePaginationChange = ({ pageIndex: newPageIndex }, { pageIndex: oldPageIndex }) => {
    if (newPageIndex !== oldPageIndex) {
      scrollToTop();
    }
  };

  const generateColumns = useCallback(
    (columnHelper: ColumnHelper<ProfileCustomValue | ActivityCustomValue>) => [
      columnHelper.accessor(({ value, text }) => value || text, {
        id: "name",
        header: t("skill"),
        cell: (info) => {
          const skill = info.row.original;
          const isMissing = (skill as ProfileCustomValue).experience === undefined;

          return !isMissing ? (
            <ProfileCustomValuePill
              customValue={skill as ProfileCustomValue}
              customType={skillsType!}
              profileId={profileId}
              maxNameLength={maxNameLength}
              allowPreview={allowSkillPreview}
              canEdit
            />
          ) : (
            <CustomValuePill
              name={String(skill.value)}
              experienceLevels={experienceLevels}
              requiredExperience={(skill as ActivityCustomValue).requiredExperience}
              experienceLabelMap={experienceLabelMap}
              maxNameLength={maxNameLength}
              tags={getCustomValueTags(skill.tagIds)}
              onClick={canEditSkills ? () => onMissingSkillClick(skill.id) : undefined}
            />
          );
        }
      }),
      columnHelper.accessor("createdAt", {
        header: t("added"),
        cell: (info) => {
          const addedOn = info.getValue();
          const skill = info.row.original;

          if (addedOn) {
            return <Typography variant="bodyRegular">{formatDate(moment(addedOn))}</Typography>;
          }

          if (canEditSkills) {
            return <Button text={t("add")} onClick={() => onMissingSkillClick(skill.id)} />;
          }

          return <Typography variant="bodyRegular">-</Typography>;
        },
        size: 100
      })
    ],
    [
      t,
      skillsType,
      profileId,
      maxNameLength,
      allowSkillPreview,
      experienceLevels,
      getCustomValueTags,
      canEditSkills,
      onMissingSkillClick,
      formatDate
    ]
  );

  return (
    <div ref={tableRef}>
      <Table<ProfileCustomValue | ActivityCustomValue>
        compact
        tableData={skills}
        initialSorting={noInitialSorting ? undefined : [{ id: "name", desc: false }]}
        controlledSorting={controlledSorting}
        onSortingChange={handleSortingChange}
        manualSorting={false}
        generateColumns={generateColumns}
        enableRowSelection={canEditSkills}
        enableMultiRowSelection
        getRowId={(skill) => String(skill.id)}
        rowSelection={rowSelectionState}
        onRowSelectionChange={setRowSelectionState}
        className={className}
        simplePaginationEnabled
        onSimplePaginationChange={handleSimplePaginationChange}
        disableAutoResizing
        disableInternalScrolling
      />
    </div>
  );
};
